import { useEffect } from 'react';
import { useAuth } from '@/app/providers';
import { useAuthModal } from './authModalStore';
import { SignInPanel } from './components/SignInPanel';

/**
 * Global sign-in overlay (the OG `#authOverlay`). Backdrop blur, closes on Esc /
 * backdrop click, and auto-closes once a session exists. Rendered once near the
 * top of the tree (RootLayout) so it floats over whatever page is underneath.
 */
export function AuthModal() {
  const open = useAuthModal((s) => s.open);
  const closeAuth = useAuthModal((s) => s.closeAuth);
  const { user } = useAuth();

  // Dismiss automatically the moment the user is authenticated.
  useEffect(() => {
    if (user && open) closeAuth();
  }, [user, open, closeAuth]);

  // Esc to close + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAuth();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, closeAuth]);

  if (!open) return null;

  return (
    <div
      className="animate-fade-in fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-5 backdrop-blur-md"
      onClick={closeAuth}
      role="dialog"
      aria-modal="true"
      aria-label="Sign in"
    >
      <div className="w-full max-w-[560px]" onClick={(e) => e.stopPropagation()}>
        <SignInPanel onClose={closeAuth} />
      </div>
    </div>
  );
}
